import { AlertCircle, CircleHelp } from 'lucide-react';

/**
 * RadioCardGroup - Premium radio selection with card UI
 * Perfect for binary or categorical choices
 */
const RadioCardGroup = ({
  label,
  options,
  value,
  onChange,
  required = false,
  error,
  helpText,
  emphasized = false,
  compact = false,
  conversational = false,
  onOptionHelpClick,
  activeHelpValue,
}) => {
  const cardPadding = conversational
    ? '11px 14px'
    : compact
      ? '12px 14px'
      : emphasized
        ? '18px 20px'
        : '16px';
  const labelSize = conversational ? '15px' : emphasized ? '17px' : '16px';
  const optionGap = conversational ? '6px' : compact ? '8px' : emphasized ? 'var(--lk-space-3)' : 'var(--lk-space-2)';
  const cardRadius = conversational ? '10px' : 'var(--lk-radius-md)';
  const selectedBorder = conversational ? '1px solid var(--lk-primary-500)' : '2px solid var(--lk-primary-500)';
  const defaultBorder = conversational ? '1px solid var(--lk-border-subtle, #e2e8f0)' : '1px solid var(--lk-border-default)';
  const radioSize = conversational ? '18px' : '20px';
  const optionRowGap = conversational ? '12px' : 'var(--lk-space-3)';
  const descSize = conversational ? '13px' : '14px';
  const selectedWeight = conversational ? 500 : 600;

  return (
    <div style={{ marginBottom: emphasized || conversational ? 0 : '32px' }}>
      {/* Label */}
      {label && (
        <label style={{
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--lk-text-primary)',
          marginBottom: '12px',
          display: 'block',
        }}>
          {label}
          {required && <span style={{ color: 'var(--lk-danger-500)', marginLeft: '4px' }}>*</span>}
        </label>
      )}

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: optionGap }}>
        {options.map((option) => {
          const isSelected = value === option.value;
          const isHelpActive = activeHelpValue === option.value;
          const showHelp = conversational && onOptionHelpClick;

          return (
            <div
              key={option.value}
              onClick={() => onChange({ target: { value: option.value } })}
              style={{
                backgroundColor: isSelected
                  ? (conversational ? 'rgba(59, 111, 217, 0.06)' : 'var(--lk-primary-50)')
                  : (conversational ? 'transparent' : 'white'),
                border: isSelected ? selectedBorder : defaultBorder,
                borderRadius: cardRadius,
                padding: cardPadding,
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                outline: isHelpActive ? '2px solid rgba(59, 111, 217, 0.25)' : 'none',
                outlineOffset: isHelpActive ? '1px' : '0',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = conversational
                    ? 'var(--lk-border-default)'
                    : 'var(--lk-border-strong)';
                  e.currentTarget.style.backgroundColor = conversational
                    ? 'rgba(0, 0, 0, 0.02)'
                    : 'var(--lk-gray-50)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = conversational
                    ? 'var(--lk-border-subtle, #e2e8f0)'
                    : 'var(--lk-border-default)';
                  e.currentTarget.style.backgroundColor = conversational ? 'transparent' : 'white';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: optionRowGap }}>
                {/* Radio Circle */}
                <div style={{
                  width: radioSize,
                  height: radioSize,
                  borderRadius: '50%',
                  border: isSelected
                    ? (conversational ? '5px solid var(--lk-primary-500)' : '6px solid var(--lk-primary-500)')
                    : (conversational ? '1.5px solid var(--lk-border-default)' : '2px solid var(--lk-border-default)'),
                  flexShrink: 0,
                  transition: 'border 0.15s ease',
                }} />

                {/* Icon (if provided) */}
                {(option.icon || option.iconClass) && (
                  <div style={{ fontSize: '24px', flexShrink: 0, color: isSelected ? 'var(--lk-primary-500)' : 'var(--lk-text-tertiary)' }}>
                    {option.icon || <i className={`ti ${option.iconClass}`}></i>}
                  </div>
                )}

                {/* Label & Description */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: labelSize,
                    fontWeight: isSelected ? selectedWeight : 400,
                    color: 'var(--lk-text-primary)',
                    marginBottom: option.description ? '2px' : 0,
                    lineHeight: 1.4,
                  }}>
                    {option.label}
                  </div>
                  {option.description && (
                    <div style={{
                      fontSize: descSize,
                      color: 'var(--lk-text-tertiary)',
                      lineHeight: 1.45,
                    }}>
                      {option.description}
                    </div>
                  )}
                </div>

                {showHelp && (
                  <button
                    type="button"
                    className={`lk-conv-option-help${isHelpActive ? ' lk-conv-option-help--active' : ''}`}
                    aria-label={`What does ${option.label} mean?`}
                    aria-pressed={isHelpActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOptionHelpClick(option);
                    }}
                  >
                    <CircleHelp size={15} strokeWidth={2} aria-hidden />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help Text or Error */}
      {error ? (
        <div style={{
          fontSize: '13px',
          color: '#dc3545',
          marginTop: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <AlertCircle size={14} />
          {error}
        </div>
      ) : helpText ? (
        <div style={{
          fontSize: '13px',
          color: '#6c757d',
          marginTop: '8px',
        }}>
          {helpText}
        </div>
      ) : null}
    </div>
  );
};

export default RadioCardGroup;
